import { z } from 'zod';
/**
 * Contrats des images extraites (FR-25). L'octet (PNG) est déposé directement dans le stockage
 * objet via une URL pré-signée : l'API décide qui a le droit, jamais par où passe le fichier
 * (Architecture §4, ADR-0010).
 */
/** Demande d'extraction : l'API réserve la ligne et renvoie une URL de dépôt. */
export const createExtractedImageSchema = z.object({
    /** Décalage en ms depuis le début de la vidéo (comme les annotations). */
    timestampMs: z.number().int().nonnegative(),
    width: z.number().int().positive().max(16384),
    height: z.number().int().positive().max(16384),
    contentType: z.literal('image/png').default('image/png'),
});
/** Réponse d'ouverture : l'URL pré-signée n'est valable qu'un temps borné. */
export const extractedImageUploadSchema = z.object({
    id: z.string(),
    uploadUrl: z.string().url(),
    /** URL de dépôt d'une **vignette** (JPEG réduit) servie dans les listes : l'aperçu reste léger là
     * où le PNG plein format pèse plusieurs méga-octets. Facultative à l'usage (dépôt au mieux). */
    thumbUploadUrl: z.string().url(),
    expiresAt: z.coerce.date(),
});
/** Confirmation du dépôt. `hasThumbnail` : vrai si la vignette a bien été déposée (sinon aperçu = PNG). */
export const confirmExtractedImageSchema = z.object({
    hasThumbnail: z.boolean().default(false),
});
/**
 * Vue d'une image extraite. `url` est une URL de lecture pré-signée (courte durée) ;
 * `annotationCount` compte les boîtes posées sur cette frame (même instant, même vidéo).
 */
export const extractedImageSchema = z.object({
    id: z.string(),
    videoId: z.string(),
    videoTitle: z.string(),
    /** Campagne dont dépend l'image, par remontée vidéo → session de vol → campagne. */
    campaignId: z.string(),
    campaignName: z.string(),
    timestampMs: z.number().int().nonnegative(),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    url: z.string().url(),
    /** URL pré-signée de la **vignette** (JPEG réduit) pour les aperçus ; `null` si aucune (images
     * antérieures à la génération de vignettes) — l'appelant retombe alors sur `url`. */
    thumbUrl: z.string().url().nullable(),
    annotationCount: z.number().int().nonnegative(),
    createdById: z.string().nullable(),
    createdByName: z.string().nullable(),
    createdAt: z.coerce.date(),
});
/**
 * Export d'un lot d'images extraites au format Ultralytics YOLO (FR-28).
 *
 * L'API ne renvoie **que** les données qu'elle possède — le mapping classe→indice, le contenu des
 * étiquettes et le `data.yaml` — plus une URL de lecture pré-signée par image. Le navigateur
 * télécharge les octets directement depuis le stockage et assemble le `.zip` : l'octet image ne
 * transite jamais par l'API (Architecture §4, ADR-0012).
 */
export const yoloExportRequestSchema = z.object({
    imageIds: z.array(z.string().cuid2()).min(1).max(1000),
});
/**
 * Demande de pré-annotation d'un lot d'images extraites (ADR-0013). L'API met les jobs en file ;
 * le worker écrit des `Annotation(source=MODEL)` que l'utilisateur validera ensuite.
 */
export const preannotateImagesSchema = z.object({
    imageIds: z.array(z.string().cuid2()).min(1).max(500),
});
/** Réponse : nombre de jobs effectivement mis en file (les doublons en cours sont ignorés). */
export const preannotateResponseSchema = z.object({
    queued: z.number().int().nonnegative(),
});
export const yoloExportManifestSchema = z.object({
    /** Noms de classes triés ; la position dans le tableau **est** l'indice YOLO. */
    classes: z.array(z.string()),
    /** Contenu du fichier `data.yaml` (config du jeu de données Ultralytics). */
    dataYaml: z.string(),
    images: z.array(z.object({
        id: z.string(),
        /** Nom de base commun à l'image et à son étiquette, unique dans le lot. */
        filename: z.string(),
        /** URL de lecture pré-signée (courte durée) du PNG à télécharger. */
        url: z.string().url(),
        /** Contenu de l'étiquette `.txt` YOLO ; vide pour une image sans boîte (image de fond). */
        label: z.string(),
    })),
});
//# sourceMappingURL=extracted-image.js.map